/**
 * Find Right Ride – collection tabs, slider & product card variants
 */
(function () {
  'use strict';

  function initSection(root) {
    if (!root || root.dataset.frrInitialized === 'true') return;
    root.dataset.frrInitialized = 'true';

    const slidesPerView = parseInt(root.dataset.slidesPerView, 10) || 4;

    initTabs(root, slidesPerView);
    initCards(root);
    root.querySelectorAll('[data-frr-slider]').forEach((slider) => {
      initSlider(slider, slidesPerView);
    });
  }

  function initTabs(root, slidesPerView) {
    const tabs = root.querySelectorAll('[data-frr-tab]');
    const panels = root.querySelectorAll('[data-frr-panel]');
    if (!tabs.length) return;

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const index = tab.getAttribute('data-tab-index');

        tabs.forEach((t) => {
          const active = t.getAttribute('data-tab-index') === index;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', active ? 'true' : 'false');
        });

        panels.forEach((panel) => {
          const active = panel.getAttribute('data-tab-index') === index;
          panel.classList.toggle('is-active', active);
          panel.hidden = !active;

          if (active) {
            const slider = panel.querySelector('[data-frr-slider]');
            if (slider && slider._frrGoTo) {
              slider._frrGoTo(0);
              slider._frrUpdateButtons();
            }
          }
        });
      });
    });
  }

  function initSlider(slider, slidesPerView) {
    if (!slider || slider.dataset.frrSliderReady === 'true') return;

    const track = slider.querySelector('[data-frr-slider-track]');
    const prevBtn = slider.querySelector('[data-frr-slider-prev]');
    const nextBtn = slider.querySelector('[data-frr-slider-next]');
    const slides = () => slider.querySelectorAll('.find-right-ride__slide');

    if (!track || !slides().length) return;

    let pageIndex = 0;

    const getSlidesPerView = () => {
      const width = window.innerWidth;
      if (width <= 767) return 1;
      if (width <= 1199) return 2;
      return Math.min(slidesPerView, 4);
    };

    const getPageCount = () => {
      const perView = getSlidesPerView();
      const total = slides().length;
      return Math.max(1, Math.ceil(total / perView));
    };

    const goTo = (index) => {
      const pageCount = getPageCount();
      pageIndex = Math.max(0, Math.min(index, pageCount - 1));
      const perView = getSlidesPerView();
      const slideEl = slides()[0];
      if (!slideEl) return;

      const gap = parseFloat(getComputedStyle(track).gap) || 20;
      const slideWidth = slideEl.offsetWidth + gap;
      const offset = pageIndex * slideWidth * perView;

      track.style.transform = `translate3d(-${offset}px, 0, 0)`;
    };

    const updateButtons = () => {
      const pageCount = getPageCount();
      const perView = getSlidesPerView();
      const total = slides().length;

      if (prevBtn) {
        prevBtn.disabled = pageIndex <= 0;
        prevBtn.hidden = total <= perView;
      }
      if (nextBtn) {
        nextBtn.disabled = pageIndex >= pageCount - 1;
        nextBtn.hidden = total <= perView;
      }
    };

    slider._frrGoTo = goTo;
    slider._frrUpdateButtons = updateButtons;

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        goTo(pageIndex - 1);
        updateButtons();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        goTo(pageIndex + 1);
        updateButtons();
      });
    }

    const onResize = debounce(() => {
      const pageCount = getPageCount();
      if (pageIndex > pageCount - 1) pageIndex = pageCount - 1;
      goTo(pageIndex);
      updateButtons();
    }, 120);

    window.addEventListener('resize', onResize);

    goTo(0);
    updateButtons();
    slider.dataset.frrSliderReady = 'true';
  }

  function debounce(fn, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(null, args), delay);
    };
  }

  function initCards(root) {
    root.querySelectorAll('[data-frr-card]').forEach((card) => {
      if (card.dataset.frrCardReady === 'true') return;
      card.dataset.frrCardReady = 'true';

      card.querySelectorAll('[data-frr-swatch]').forEach((swatch) => {
        swatch.addEventListener('click', () => selectVariant(card, swatch));
      });

      const atc = card.querySelector('[data-frr-atc]');
      if (atc) {
        const labelEl = atc.querySelector('staged-action-text') || atc;
        if (!labelEl.dataset.frrAtcDefault) {
          labelEl.dataset.frrAtcDefault = labelEl.textContent.trim();
        }
      }
    });
  }

  function selectVariant(card, swatch) {
    const variantId = swatch.getAttribute('data-variant-id');
    const imageUrl = swatch.getAttribute('data-variant-image');
    const productUrl = swatch.getAttribute('data-variant-url');
    const priceFormatted = swatch.getAttribute('data-variant-price-formatted');
    const compareFormatted = swatch.getAttribute('data-variant-compare-formatted');
    const saveLabel = swatch.getAttribute('data-variant-save-label');
    const savePercent = swatch.getAttribute('data-variant-save-percent');
    const available = swatch.getAttribute('data-variant-available') === 'true';

    card.querySelectorAll('[data-frr-swatch]').forEach((btn) => {
      btn.classList.toggle('is-active', btn === swatch);
    });

    const input = card.querySelector('[data-frr-variant-input]');
    if (input && variantId) input.value = variantId;

    const wishlist = card.querySelector('[data-frr-wishlist]');
    if (wishlist && variantId) wishlist.setAttribute('data-variant-id', variantId);

    const image = card.querySelector('[data-frr-image]');
    if (image && imageUrl) {
      image.src = imageUrl;
      image.removeAttribute('srcset');
    }

    const imageLink = card.querySelector('[data-frr-image-link]');
    const titleLink = card.querySelector('[data-frr-title-link]');
    if (productUrl) {
      if (imageLink) imageLink.href = productUrl;
      if (titleLink) titleLink.href = productUrl;
    }

    const priceEl = card.querySelector('[data-frr-price]');
    if (priceEl && priceFormatted) priceEl.textContent = priceFormatted;

    const compareEl = card.querySelector('[data-frr-compare]');
    if (compareEl) {
      if (compareFormatted) {
        compareEl.textContent = compareFormatted;
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }

    const saveBadge = card.querySelector('[data-frr-save-badge]');
    const saveLabelEl = card.querySelector('[data-frr-save-label]');
    const savePercentEl = card.querySelector('[data-frr-save-percent]');
    const offerEl = card.querySelector('[data-frr-offer]');

    if (saveBadge && saveLabelEl && savePercentEl) {
      if (saveLabel && savePercent) {
        saveLabelEl.textContent = saveLabel;
        savePercentEl.textContent = savePercent;
        saveBadge.hidden = false;
        if (offerEl) offerEl.hidden = false;
      } else {
        saveBadge.hidden = true;
        if (offerEl) offerEl.hidden = true;
      }
    }

    const atc = card.querySelector('[data-frr-atc]');
    if (atc) {
      const labelEl = atc.querySelector('staged-action-text') || atc;
      atc.disabled = !available;
      labelEl.textContent = available
        ? labelEl.dataset.frrAtcDefault || 'Add to Cart'
        : 'Sold Out';
    }
  }

  function initAll() {
    document.querySelectorAll('[data-find-right-ride]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector('[data-find-right-ride]');
    if (root) initSection(root);
  });
})();
(function () {
  'use strict';
  function addSwipe(slider) {
    if (!slider || slider.dataset.frrSwipe === 'true') return;
    var vp = slider.querySelector('.find-right-ride__slider-viewport');
    var prev = slider.querySelector('[data-frr-slider-prev]');
    var next = slider.querySelector('[data-frr-slider-next]');
    if (!vp || !prev || !next) return;

    var x0 = 0, y0 = 0, locked = null;

    vp.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) return;
      x0 = e.touches[0].clientX;
      y0 = e.touches[0].clientY;
      locked = null;
    }, { passive: true });

    vp.addEventListener('touchmove', function (e) {
      if (locked !== null || e.touches.length !== 1) return;
      var dx = e.touches[0].clientX - x0;
      var dy = e.touches[0].clientY - y0;
      if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
        locked = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
    }, { passive: true });

    vp.addEventListener('touchend', function (e) {
      if (locked !== 'x') return;
      var dx = e.changedTouches[0].clientX - x0;
      if (Math.abs(dx) < 45) return;
      var btn = dx < 0 ? next : prev;
      if (!btn.disabled && !btn.hidden) btn.click();
    }, { passive: true });

    vp.style.touchAction = 'pan-y';
    slider.dataset.frrSwipe = 'true';
  }

  function initSwipeAll() {
    document.querySelectorAll('[data-frr-slider]').forEach(addSwipe);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSwipeAll);
  } else {
    initSwipeAll();
  }
  document.addEventListener('shopify:section:load', initSwipeAll);
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-frr-tab]')) setTimeout(initSwipeAll, 100);
  });
})();